import { memo, useCallback, useRef } from "react"
import { Loader2, Pencil } from "lucide-react"
import { TimelineFilmstrip } from "@/components/video-timeline-ui"
import { cn } from "@/lib/utils"
import { clipDuration, clipEnd, clipSpeed } from "./timeline-model"
import { WaveformCanvas } from "./waveform-canvas"
import type { TimelineClip, TimelineSource, TimelineWaveform, TrackKind } from "./types"

type DragState =
  | { mode: "move"; startX: number; origStartMs: number }
  | { mode: "resize-left"; startX: number; origEdgeMs: number }
  | { mode: "resize-right"; startX: number; origEdgeMs: number }
  | { mode: "rate-left"; startX: number; origEdgeMs: number }
  | { mode: "rate-right"; startX: number; origEdgeMs: number }
  | { mode: "fade-in"; startX: number; origFadeMs: number }
  | { mode: "fade-out"; startX: number; origFadeMs: number }
  | { mode: "activate"; startX: number }

type Props = {
  clip: TimelineClip
  trackKind: TrackKind
  pxPerMs: number
  selected: boolean
  muted: boolean
  /** Клип заморожен (идёт lipsync-джоба): без выделения и drag, overlay со спиннером. */
  locked?: boolean
  /** Тело клипа нельзя перетаскивать (correction); края resize по-прежнему работают. */
  moveDisabled?: boolean
  /** Источник клипа (per-clip override или версия дорожки) — для фонового filmstrip. */
  source?: TimelineSource | null
  /** Клик по телу клипа (correction): открыть модалку. */
  onActivateClip?: (clipId: string) => void
  /** aria/title для карандаша на correction-клипе. */
  activateLabel?: string
  /** Имя аудио-ассета для custom_audio клипов. */
  assetName?: string | null
  /** Серверные пики waveform (asset или source); без них — CSS-fallback. */
  waveform?: TimelineWaveform | null
  /** Эффективная громкость дорожки 0..1 (mute = 0) — масштабирует амплитуду волны. */
  volumeGain?: number
  /**
   * Только video: gain компактной полу-гистограммы внизу клипа (0 при mute — волна
   * оседает и растворяется); null — не показывать (audio_detached / hidden).
   */
  videoWaveformGain?: number | null
  /** additive=true — cmd/ctrl+клик: toggle в множественном выделении. */
  onSelect: (clipId: string, additive: boolean) => void
  onMove: (clipId: string, newStartMs: number) => void
  onResize: (clipId: string, edge: "left" | "right", newEdgeMs: number) => void
  /** Alt+drag края: изменение скорости (rate-stretch) вместо трима. */
  onRateStretch?: (clipId: string, edge: "left" | "right", newEdgeMs: number) => void
  onFade?: (clipId: string, edge: "in" | "out", fadeMs: number) => void
  onDragEnd: () => void
}

/**
 * Клип на дорожке: drag тела — перемещение, drag краёв — восстановление/подрезка
 * source-диапазона (с Alt — rate-stretch), ручки в верхних углах аудио-клипов — фейды.
 * Correction: тело без move, клик открывает модалку; карандаш справа сверху.
 */
export const ClipView = memo(function ClipView({
  clip,
  trackKind,
  pxPerMs,
  selected,
  muted,
  locked = false,
  moveDisabled = false,
  source,
  onActivateClip,
  activateLabel,
  assetName,
  waveform = null,
  volumeGain = 1,
  videoWaveformGain = null,
  onSelect,
  onMove,
  onResize,
  onRateStretch,
  onFade,
  onDragEnd,
}: Props) {
  const dragRef = useRef<DragState | null>(null)
  const movedRef = useRef(false)

  const isAudio = trackKind !== "video"
  const speed = clipSpeed(clip)
  const durMs = clipDuration(clip)
  const leftPx = clip.timeline_start_ms * pxPerMs
  const widthPx = Math.max(durMs * pxPerMs, 6)
  const fadeIn = clip.fade_in_ms ?? 0
  const fadeOut = clip.fade_out_ms ?? 0

  const startDrag = useCallback(
    (e: React.PointerEvent, state: DragState) => {
      if (e.button !== 0 || locked) return
      e.preventDefault()
      e.stopPropagation()
      const additive = e.metaKey || e.ctrlKey
      onSelect(clip.id, additive)
      // cmd/ctrl+клик — только выделение: drag при additive-клике не начинаем.
      if (additive) return
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      dragRef.current = state
      movedRef.current = false
    },
    [onSelect, clip.id, locked],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const deltaMs = (e.clientX - drag.startX) / pxPerMs
      if (Math.abs(e.clientX - drag.startX) > 2) movedRef.current = true
      switch (drag.mode) {
        case "move":
          onMove(clip.id, drag.origStartMs + deltaMs)
          break
        case "activate":
          break
        case "resize-left":
          onResize(clip.id, "left", drag.origEdgeMs + deltaMs)
          break
        case "resize-right":
          onResize(clip.id, "right", drag.origEdgeMs + deltaMs)
          break
        case "rate-left":
          onRateStretch?.(clip.id, "left", drag.origEdgeMs + deltaMs)
          break
        case "rate-right":
          onRateStretch?.(clip.id, "right", drag.origEdgeMs + deltaMs)
          break
        case "fade-in":
          onFade?.(clip.id, "in", drag.origFadeMs + deltaMs)
          break
        case "fade-out":
          onFade?.(clip.id, "out", drag.origFadeMs - deltaMs)
          break
      }
    },
    [pxPerMs, onMove, onResize, onRateStretch, onFade, clip.id],
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return
      const drag = dragRef.current
      dragRef.current = null
      ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
      if (drag.mode === "activate" && !movedRef.current && onActivateClip) {
        onActivateClip(clip.id)
        return
      }
      if (movedRef.current) onDragEnd()
    },
    [onDragEnd, onActivateClip, clip.id],
  )

  const onBodyPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (moveDisabled && onActivateClip) {
        startDrag(e, { mode: "activate", startX: e.clientX })
        return
      }
      if (moveDisabled) {
        // Move forbidden but no activate handler: select only.
        if (e.button !== 0 || locked) return
        e.preventDefault()
        e.stopPropagation()
        onSelect(clip.id, e.metaKey || e.ctrlKey)
        return
      }
      startDrag(e, { mode: "move", startX: e.clientX, origStartMs: clip.timeline_start_ms })
    },
    [moveDisabled, onActivateClip, startDrag, locked, onSelect, clip.id, clip.timeline_start_ms],
  )

  return (
    <div
      className={cn(
        "group/clip absolute inset-y-0 touch-none overflow-hidden rounded-md border select-none",
        selected ? "z-10 border-ui-accent ring-1 ring-ui-accent" : "border-ui-border-strong",
        isAudio
          ? trackKind === "custom_audio"
            ? "bg-emerald-950/80 dark:bg-emerald-950/80 light:bg-emerald-100"
            : "bg-sky-950/80 dark:bg-sky-950/80 light:bg-sky-100"
          : "bg-black",
        // Аудио-клип гаснет по прототипу «Громкость дорожки»: opacity .42, переход .18s.
        isAudio && "transition-opacity duration-[180ms]",
        muted && (isAudio ? "opacity-[0.42]" : "opacity-50"),
        locked && "cursor-not-allowed",
        !locked && moveDisabled && onActivateClip && "cursor-pointer",
      )}
      style={{ left: leftPx, width: widthPx }}
      title={moveDisabled && onActivateClip ? activateLabel : undefined}
      onPointerDown={onBodyPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {trackKind === "video" && source ? (
        <div className="pointer-events-none absolute inset-0 flex">
          <TimelineFilmstrip
            frameUrls={source.timeline_frame_urls}
            frameIntervalMs={
              source.duration_ms && source.timeline_frame_urls.length > 0
                ? source.duration_ms / source.timeline_frame_urls.length
                : undefined
            }
            aspectRatio={source.aspect_ratio ?? 16 / 9}
            rangeStartMs={clip.source_in_ms}
            rangeEndMs={clip.source_out_ms}
            innerHeight={46}
            variant={source.timeline_frame_urls.length > 0 ? "filmstrip" : "frameTicks"}
          />
        </div>
      ) : null}

      {/* Компактная аудио-гистограмма видеоклипа: «верхняя половина» зеркальной волны
          у нижней кромки. При mute оседает (gain 0) и растворяется; на клипах без
          аудиопотока (например, lipsync-результат) пиков нет — ничего не рисуем. */}
      {trackKind === "video" && videoWaveformGain != null && waveform && waveform.peaks.length > 0 ? (
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[14px] transition-opacity duration-[180ms]",
            videoWaveformGain === 0 ? "opacity-0" : "opacity-100",
          )}
        >
          {/* Тёмная подложка снизу вверх: без неё волна теряется на светлых кадрах filmstrip. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
          <WaveformCanvas
            waveform={waveform}
            sourceInMs={clip.source_in_ms}
            sourceOutMs={clip.source_out_ms}
            widthPx={widthPx}
            heightPx={10}
            gain={videoWaveformGain}
            anchor="bottom"
            className="absolute inset-x-0 bottom-0 h-[10px] text-ui-text-secondary opacity-55"
          />
        </div>
      ) : null}

      {isAudio ? (
        <>
          {waveform && waveform.peaks.length > 0 ? (
            <WaveformCanvas
              waveform={waveform}
              sourceInMs={clip.source_in_ms}
              sourceOutMs={clip.source_out_ms}
              widthPx={widthPx}
              // Дорожка h-12 (~48px); ~34px — почти вся высота, сверху место под имя.
              heightPx={34}
              gain={volumeGain}
              className={cn(
                "pointer-events-none absolute inset-x-0 top-1/2 h-[34px] -translate-y-1/2 opacity-55 transition-colors duration-[180ms]",
                volumeGain === 0 ? "text-ui-text-muted" : "text-ui-text-secondary",
              )}
            />
          ) : (
            /* Fallback: ровные вертикальные штрихи, пока пиков нет. */
            <div
              className="pointer-events-none absolute inset-x-0 top-1/2 h-[34px] -translate-y-1/2 opacity-40"
              style={{
                backgroundImage: "repeating-linear-gradient(90deg, currentColor 0 1px, transparent 1px 4px)",
              }}
            />
          )}
          <span className="pointer-events-none absolute inset-x-1.5 top-0.5 truncate text-[9px] leading-tight text-ui-text-secondary">
            {assetName ?? ""}
          </span>
          {/* Заливка фейдов */}
          {fadeIn > 0 ? (
            <div
              className="pointer-events-none absolute inset-y-0 left-0 bg-black/35"
              style={{
                width: fadeIn * pxPerMs,
                clipPath: "polygon(0 0, 100% 0, 0 100%)",
              }}
            />
          ) : null}
          {fadeOut > 0 ? (
            <div
              className="pointer-events-none absolute inset-y-0 right-0 bg-black/35"
              style={{
                width: fadeOut * pxPerMs,
                clipPath: "polygon(0 0, 100% 0, 100% 100%)",
              }}
            />
          ) : null}
          {/* Ручки фейдов */}
          {onFade ? (
            <>
              <div
                className={cn(
                  "absolute top-0 z-30 h-2.5 w-2.5 -translate-x-1/2 cursor-ew-resize rounded-full border border-white/70 bg-ui-accent opacity-0 transition-opacity",
                  "group-hover/clip:opacity-100",
                  selected && "opacity-100",
                )}
                style={{ left: Math.min(fadeIn * pxPerMs, widthPx) }}
                onPointerDown={(e) => startDrag(e, { mode: "fade-in", startX: e.clientX, origFadeMs: fadeIn })}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
              <div
                className={cn(
                  "absolute top-0 z-30 h-2.5 w-2.5 translate-x-1/2 cursor-ew-resize rounded-full border border-white/70 bg-ui-accent opacity-0 transition-opacity",
                  "group-hover/clip:opacity-100",
                  selected && "opacity-100",
                )}
                style={{ right: Math.min(fadeOut * pxPerMs, widthPx) }}
                onPointerDown={(e) => startDrag(e, { mode: "fade-out", startX: e.clientX, origFadeMs: fadeOut })}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
            </>
          ) : null}
        </>
      ) : null}

      {/* Заморозка на время lipsync-джобы */}
      {locked ? (
        <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-black/55 backdrop-blur-[1px]">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-white/85" />
        </div>
      ) : null}

      {/* Correction: affordance открытия модалки */}
      {onActivateClip && !locked ? (
        <span
          className="pointer-events-none absolute top-0.5 right-0.5 z-20 flex h-4 w-4 items-center justify-center rounded-sm bg-black/55 text-white/90"
          aria-hidden
        >
          <Pencil className="h-2.5 w-2.5" />
        </span>
      ) : null}

      {/* Бейдж скорости */}
      {speed !== 1 ? (
        <span
          className={cn(
            "pointer-events-none absolute right-1 bottom-0.5 z-20 rounded-sm bg-black/60 px-1 text-[9px] leading-tight font-medium tabular-nums",
            speed > 1 ? "text-amber-300" : "text-sky-300",
          )}
        >
          {speed}x
        </span>
      ) : null}

      {/* Края: восстановление/подрезка source-диапазона; с Alt — rate-stretch */}
      <div
        className="absolute inset-y-0 left-0 z-20 w-1.5 cursor-ew-resize bg-transparent transition-colors hover:bg-ui-accent/60"
        onPointerDown={(e) =>
          startDrag(e, {
            mode: e.altKey && onRateStretch ? "rate-left" : "resize-left",
            startX: e.clientX,
            origEdgeMs: clip.timeline_start_ms,
          })
        }
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      <div
        className="absolute inset-y-0 right-0 z-20 w-1.5 cursor-ew-resize bg-transparent transition-colors hover:bg-ui-accent/60"
        onPointerDown={(e) =>
          startDrag(e, {
            mode: e.altKey && onRateStretch ? "rate-right" : "resize-right",
            startX: e.clientX,
            origEdgeMs: clipEnd(clip),
          })
        }
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  )
})
